/**
 * 62.48: the turn clock's bot knobs — every number and switch of the turn bot's tuning (`BotTuning`, src/ai/bot/tuning.ts:
 * the scoring, targeting, weapon, scouting, fire search and its tiers, movement, jumping, economy, flow, seats, stealth
 * and team-picture sections) as `turn.tuning.<section>.<key>…` rows, flattened from DEFAULT_BOT_TUNING itself — the
 * base tuning. A bot's skill level lays its profile (SKILL_PROFILES) over that base; the profiles are rows of their own
 * (`turn.profile.<level>.…`). See withTurnParams for the layering.
 */
import type { BotConfig, BotParamDef, BotParamSet, BotParamValue, BotTuning, BotTuningOverrides } from '../types';
import { DEFAULT_BOT_TUNING, SKILL_PROFILES, skillLevelFor } from './bot/tuning';
import { atPath, catalogueOf, entriesUnder, withPath, type RangeRule } from './botParamsLib';

const READERS: Record<keyof BotTuning, string> = {
  action: 'bot/decision chooseBotAction',
  postFire: 'bot/decision chooseBotPostFireAction',
  targeting: 'bot/targeting selectTarget',
  weapon: 'bot/targeting selectWeapon',
  scouting: 'bot/scouting shouldScoutProbe, computeScoutProbe',
  fire: 'bot/fireSearch computeBotFire',
  movement: 'bot/movement computeBotMove',
  jumping: 'bot/jumping computeBotJump, transitGraph',
  economy: 'bot/economy computeBotShop, computeBotLoadout',
  intel: 'bot/fireSearch getTeammateFireSeeds',
  flow: 'botTurnRunner, bot/targeting, bot/fireSearch (round flows)',
  mounting: 'botBehaviorGraphs (seats)',
  stealth: 'bot/sneak, bot/jumping',
  asks: 'core/callIn dangerClose (via botTurnRunner)',
  picture: 'botFogContext (team-picture targets)',
};

/** Ranges the name rule gets wrong (by exact path, or `.suffix`). */
const RULES: Record<string, RangeRule> = {
  '.minSkill': { min: 0, max: 1 },
  '.elevations': { min: -1.6, max: 1.6 },
  'turn.tuning.fire.searchTiers.0.minSkill': { min: 0, max: 1 },
  'turn.tuning.weapon.overkillTargetHealth': { min: 0, max: 200 },
  'turn.tuning.weapon.overkillDamage': { min: 0, max: 200 },
};

export const TURN_PARAMS: readonly BotParamDef[] = catalogueOf('turn.tuning', DEFAULT_BOT_TUNING, path => READERS[path.split('.')[2] as keyof BotTuning] ?? 'turn bot', RULES);

/**
 * The skill levels' profiles as rows too — `turn.profile.<level>.<section>.<key>…` (SKILL_PROFILES: jump reach, careful
 * landings, the expert's flow and fire): a level's profile sits over the base tuning, so these are how a file changes it.
 */
export const TURN_PROFILE_PARAMS: readonly BotParamDef[] = catalogueOf('turn.profile', SKILL_PROFILES, path => `bot/tuning tuningFor (the ${path.split('.')[2]} profile) → ${READERS[path.split('.')[3] as keyof BotTuning] ?? 'turn bot'}`, RULES);

/**
 * A bot's tuning overrides with the catalogue's turn values, layered the way the bot resolves its tuning
 * (defaults ← its skill level's profile ← its own overrides): a `turn.tuning.…` value replaces the default under the
 * profile (so a key the profile sets keeps the profile's), a `turn.profile.<its level>.…` value replaces the profile's,
 * and the bot's own overrides (the setup's tuning options) win over both. A value deeper than a section's key (a search
 * tier's field, a weapon's preference) copies that key's value from the layer it replaces and changes one leaf. So a
 * file of every default is today's bot exactly.
 */
export function withTurnParams(values: BotParamSet | undefined, config: Pick<BotConfig, 'skill' | 'tuning' | 'skillProfile'>): BotTuningOverrides | undefined {
  const level = config.skillProfile === false ? null : skillLevelFor(config.skill);
  const base = entriesUnder(values, 'turn.tuning');
  const prof = level ? entriesUnder(values, `turn.profile.${level}`) : [];
  if (!base.length && !prof.length) return config.tuning;
  const own = (config.tuning ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const profile = (level ? SKILL_PROFILES[level] : {}) as Record<string, Record<string, unknown> | undefined>;
  const defaults = DEFAULT_BOT_TUNING as unknown as Record<string, Record<string, unknown>>;
  const out: Record<string, Record<string, unknown>> = {};
  for (const [section, o] of Object.entries(own)) if (o) out[section] = { ...o };
  const written = new Set<string>();
  const put = (section: string, key: string, rest: string[], value: BotParamValue, from: unknown) => {
    const sec = out[section] ?? (out[section] = {});
    const id = `${section}.${key}`;
    sec[key] = rest.length ? withPath(written.has(id) ? sec[key] : from, rest, value) : value;
    written.add(id);
  };
  for (const [[section, key, ...rest], value] of prof) {
    if (own[section]?.[key] !== undefined) continue;
    put(section, key, rest, value, profile[section]?.[key] ?? atPath(defaults, [section, key]));
  }
  for (const [[section, key, ...rest], value] of base) {
    if (own[section]?.[key] !== undefined || profile[section]?.[key] !== undefined) continue;   // a layer above sets it
    put(section, key, rest, value, atPath(defaults, [section, key]));
  }
  return out as BotTuningOverrides;
}
